import { type TagProps } from '@loadsmart/loadsmart-ui'

import IllustrationHourglass from 'core/ui/assets/illustration-hourglass.svg'
import IllustrationTruckFacility from 'core/ui/assets/illustration-truck-facility.svg'
import { plural } from 'core/utils/string'

import formatDate from 'legacy/utils/date/formatDate'

import { type ConfirmAwardedLaneAction } from '../domain/ConfirmAwardedLaneAction'
import { type DistanceUnit } from '../domain/DistanceUnit'
import { isLaneAwardedAccepted, isLaneAwardedConfirmed, type Lane } from '../domain/Lane'
import { hasConfirmedAwardedLanes, type Proposal } from '../domain/Proposal'
import { formatDistanceUnitShorthand } from './rfpPresenter'

export const OPTION_ACCEPTED = 'accept' as ConfirmAwardedLaneAction
export const OPTION_REJECTED = 'deny' as ConfirmAwardedLaneAction

export function getAutoSubmitLabel({ awardConfirmationDeadline }: Proposal): string {
  if (!awardConfirmationDeadline) return 'Auto submit'

  return `Auto submit on ${formatDate(awardConfirmationDeadline, 'MM/dd/yyyy @ p')}`
}

export function getAwardedProgressTitle({ lanes }: Proposal): string {
  return `You've been awarded ${lanes.length} ${plural('bid', lanes.length)!}`
}

export function getAwardedTypeInfo({ laneProposal }: Lane) {
  const { awardedType } = laneProposal!

  return {
    label: `${awardedType} contract`.toUpperCase(),
    tagVariant: awardedType === 'primary' ? 'success' : 'default',
  }
}

export function formatRates({ laneProposal }: Lane, distanceUnit: DistanceUnit, currency: string) {
  return {
    flatRate: `${currency}${laneProposal!.flatRate}`,
    perDistanceRate: `${currency}${laneProposal!.perDistanceRate}/${formatDistanceUnitShorthand(
      distanceUnit
    )}`,
  }
}

export function getAwardedVolume({ laneProposal }: Lane) {
  const { awardedVolume } = laneProposal!
  return awardedVolume ? `Volume: ${awardedVolume}` : null
}

export function getAwardedLaneConfirmedValue(lane: Lane) {
  if (!isLaneAwardedConfirmed(lane)) return undefined

  return isLaneAwardedAccepted(lane) ? OPTION_ACCEPTED : OPTION_REJECTED
}

export function getSubmitAwardedText({ lanes }: Proposal) {
  const lanesCount = lanes.length
  const acceptedCount = lanes.filter(l => l.laneProposal?.awardConfirmation === 'accepted').length
  const rejectedCount = lanes.filter(l => l.laneProposal?.awardConfirmation === 'denied').length
  const notConfirmedCount = lanesCount - (acceptedCount + rejectedCount)

  let leadingText = 'Are you sure you want to'
  let acceptedText
  let rejectedText
  let trailingText

  if (lanesCount === acceptedCount) {
    // all accepted

    acceptedText = `accept ${acceptedCount} awarded ${plural('bid', acceptedCount)}?`
  } else if (lanesCount === rejectedCount) {
    // all rejected

    rejectedText = `reject ${rejectedCount} awarded ${plural('bid', rejectedCount)}?`
  } else if (lanesCount === notConfirmedCount) {
    // all not confirmed

    leadingText = 'By submitting now, you are going to'
    rejectedText = `reject ${notConfirmedCount} awarded ${plural('bid', notConfirmedCount)}. `
    trailingText = 'Do you want to proceed?'
  } else if (lanesCount === acceptedCount + rejectedCount) {
    // some accepted and some rejected

    acceptedText = `accept ${acceptedCount} awarded ${plural('bid', acceptedCount)} and `
    rejectedText = `reject ${rejectedCount} awarded ${plural('bid', rejectedCount)}?`
  } else if (lanesCount === acceptedCount + notConfirmedCount) {
    // some accepted and some not confirmed

    leadingText = 'By submitting now, you are going to'
    acceptedText = `accept ${acceptedCount} awarded ${plural('bid', acceptedCount)} and `
    rejectedText = `reject ${notConfirmedCount} awarded ${plural('bid', notConfirmedCount)}? `
    trailingText = 'Do you want to proceed?'
  } else if (lanesCount === rejectedCount + notConfirmedCount) {
    // some rejected and some not confirmed

    leadingText = 'By submitting now, you are going to'
    rejectedText = `reject ${rejectedCount + notConfirmedCount} awarded ${plural(
      'bid',
      rejectedCount + notConfirmedCount
    )}. `
    trailingText = 'Do you want to proceed?'
  } else {
    // some accepted, some rejected, and some not confirmed

    leadingText = 'By submitting now, you are going to'
    acceptedText = `accept ${acceptedCount} awarded ${plural('bid', acceptedCount)} and `
    rejectedText = `reject ${rejectedCount + notConfirmedCount} awarded ${plural(
      'bid',
      rejectedCount + notConfirmedCount
    )}. `
    trailingText = 'Do you want to proceed?'
  }

  return {
    leadingText,
    acceptedText,
    rejectedText,
    trailingText,
  }
}

export function getConfirmationContent(awardedProposal: Proposal) {
  const {
    rfp: { shipperName },
  } = awardedProposal
  const hasConfirmedLanes = hasConfirmedAwardedLanes(awardedProposal)
  const imageSrc = hasConfirmedLanes ? IllustrationTruckFacility : IllustrationHourglass
  const title = hasConfirmedLanes ? 'Submission complete' : 'Submission expired'
  const subtitle = hasConfirmedLanes
    ? 'Thanks for your response.'
    : 'This contract is not accepting response anymore'
  const moreInformation = `For more information, please contact ${
    shipperName ? shipperName : 'shipper'
  } directly.`

  return {
    imageSrc,
    title,
    subtitle,
    moreInformation,
  }
}

export function getAwardedConfirmationInfo({ laneProposal }: Lane) {
  const { awardConfirmation } = laneProposal!

  return {
    label: awardConfirmation === 'accepted' ? 'ACCEPTED' : 'REJECTED',
    tagVariant: (awardConfirmation === 'accepted' ? 'success' : 'danger') as TagProps['variant'],
  }
}
